import React from 'react';
import { useTranslation } from 'react-i18next';
import { Contact } from '../../types/contact';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Building2, Briefcase, Users, Bot, HelpCircle } from 'lucide-react';

interface ContactsTableProps {
  contacts: Contact[];
  onEdit: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
  sortField: keyof Contact;
  sortDirection: 'asc' | 'desc';
  onSort: (field: keyof Contact) => void;
}

export const ContactsTable: React.FC<ContactsTableProps> = ({
  contacts,
  onEdit,
  onDelete,
  sortField,
  sortDirection,
  onSort
}) => {
  const { t } = useTranslation();

  // Fonction pour rendre le badge de type de contact avec l'icône appropriée
  const renderContactTypeBadge = (contactType: string) => {
    const types = {
      CLIENT: {
        icon: <Building2 className="h-3.5 w-3.5 mr-1" />,
        className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
      },
      PARTNER: {
        icon: <Briefcase className="h-3.5 w-3.5 mr-1" />,
        className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
      },
      INTERNAL: {
        icon: <Users className="h-3.5 w-3.5 mr-1" />,
        className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
      },
      SYSTEM: {
        icon: <Bot className="h-3.5 w-3.5 mr-1" />,
        className: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
      },
      UNKNOWN: {
        icon: <HelpCircle className="h-3.5 w-3.5 mr-1" />,
        className: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400"
      }
    };

    const type = types[contactType as keyof typeof types] || types.UNKNOWN;

    return (
      <Badge variant="secondary" className={type.className}>
        <div className="flex items-center">
          {type.icon}
          {t(`contact.type.${contactType.toLowerCase()}`)}
        </div>
      </Badge>
    );
  };

  // Fonction pour rendre l'entité associée (client, partenaire, etc.)
  const renderContactEntity = (contact: Contact) => {
    switch(contact.contactType) {
      case 'CLIENT':
        return contact.client ? (
          <div className="flex items-center space-x-1">
            <Building2 className="h-3.5 w-3.5 text-blue-500" />
            <span>{contact.client?.name || contact.clientName || 'N/A'}</span>
          </div>
        ) : contact.clientName ? (
          <div className="flex items-center space-x-1">
            <Building2 className="h-3.5 w-3.5 text-blue-500" />
            <span>{contact.clientName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">{t('contact.notAssociated')}</span>
        );
      
      case 'PARTNER':
        return contact.partner ? (
          <div className="flex items-center space-x-1">
            <Briefcase className="h-3.5 w-3.5 text-amber-500" />
            <span>{contact.partner?.name || contact.partnerName || 'N/A'}</span>
          </div>
        ) : contact.partnerName ? (
          <div className="flex items-center space-x-1">
            <Briefcase className="h-3.5 w-3.5 text-amber-500" />
            <span>{contact.partnerName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">{t('contact.notAssociated')}</span>
        );

      case 'INTERNAL':
        return contact.user ? (
          <div className="flex items-center space-x-1">
            <Users className="h-3.5 w-3.5 text-green-500" />
            <span>{contact.user?.name || contact.userName || t('contact.team')}</span>
          </div>
        ) : contact.userName ? (
          <div className="flex items-center space-x-1">
            <Users className="h-3.5 w-3.5 text-green-500" />
            <span>{contact.userName}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">{t('contact.team')}</span>
        );
      
      case 'SYSTEM':
        return (
          <div className="flex items-center space-x-1">
            <Bot className="h-3.5 w-3.5 text-purple-500" />
            <span>{t('contact.system')}</span>
          </div>
        );

      default:
        return (
          <div className="flex items-center space-x-1">
            <HelpCircle className="h-3.5 w-3.5 text-gray-500" />
            <span>{t('contact.unknown')}</span>
          </div>
        );
    }
  };

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead 
              className="cursor-pointer" 
              onClick={() => onSort('name')}
            >
              {t('contact.name')}
              {sortField === 'name' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
            </TableHead>
            <TableHead 
              className="cursor-pointer" 
              onClick={() => onSort('email')}
            >
              {t('contact.email')}
              {sortField === 'email' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
            </TableHead>
            <TableHead 
              className="cursor-pointer" 
              onClick={() => onSort('contactType')}
            >
              {t('contact.type.label')}
              {sortField === 'contactType' && (sortDirection === 'asc' ? ' ↑' : ' ↓')}
            </TableHead>
            <TableHead>{t('contact.entity')}</TableHead>
            <TableHead>{t('contact.role')}</TableHead>
            <TableHead>{t('contact.phone')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="h-32 text-center">
                {t('contact.noContacts')}
              </TableCell>
            </TableRow>
          ) : (
            contacts.map((contact) => (
              <TableRow key={contact.id}>
                <TableCell className="font-medium">{contact.name}</TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell>
                  {renderContactTypeBadge(contact.contactType || 'UNKNOWN')}
                </TableCell>
                <TableCell>
                  {renderContactEntity(contact)}
                </TableCell>
                <TableCell>{contact.role || '-'}</TableCell>
                <TableCell>{contact.phone || '-'}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(contact)}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(contact)}
                    className="text-destructive hover:text-destructive/90"
                  >
                    {t('common.delete')}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
